import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import completeImg from "../assets/complete-energy-group-solar-website-project.webp";
import stellImg from "../assets/stella-designs-creative-studio-project.webp";
import amreshprajapatiImg from "../assets/amresh-prajapati-web-developer-profile.png";
import logoImg from "../assets/amresh-prajapati-website-developer-logo.webp";

import {
  FaEnvelope,
  FaMapMarkerAlt,
  FaPhone,
  FaInstagram,
  FaLinkedinIn,
  FaFacebookF,
  FaWhatsapp,
  FaCode,
  FaPalette,
  FaSearch,
  FaBox,
  FaCheckCircle,
  FaReact,
  FaNodeJs,
  FaPhp,
  FaServer,
  FaGlobe,
  FaUsersCog,
  FaCogs,
  FaCubes,
  FaArrowLeft,
  FaArrowRight,
  FaStar,
  FaHome,
} from "react-icons/fa";
import { SiMysql } from "react-icons/si";
import "./Home.css";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0 },
};

const skills = [
  { name: "React JS", pct: 90, icon: <FaReact /> },
  { name: "Node.js", pct: 85, icon: <FaNodeJs /> },
  { name: "PHP", pct: 85, icon: <FaPhp /> },
  { name: "MySQL", pct: 85, icon: <SiMysql /> },
  { name: "Custom CRM", pct: 90, icon: <FaUsersCog /> },
  { name: "ERP Solutions", pct: 80, icon: <FaCubes /> },
  { name: "Domain Management", pct: 95, icon: <FaGlobe /> },
  { name: "Hosting Management", pct: 90, icon: <FaServer /> },
];

const services = [
  {
    icon: <FaPalette />,
    title: "Website Design",
    desc: "I create modern website UI designs using Figma, Canva and clean visual strategy.",
    count: "9+ Projects",
  },
  {
    icon: <FaCode />,
    title: "Custom Website Development",
    desc: "I build fast, responsive websites using React JS, Node.js, Express and PHP.",
    count: "60+ Projects",
  },
  {
    icon: <FaCogs />,
    title: "Custom CRM & ERP Solutions",
    desc: "I develop custom CRM and ERP solutions using React JS, Node.js and MySQL.",
    count: "3+ Projects",
  },
  {
    icon: <FaSearch />,
    title: "SEO / Marketing",
    desc: "I optimize websites for better search visibility, speed, structure and user engagement.",
    count: "8+ Projects",
  },
  {
    icon: <FaBox />,
    title: "Creative Packaging Design",
    desc: "I create custom, eye-catching packaging concepts that help products stand out.",
    count: "25+ Projects",
  },
];

const stats = [
  { label: "Years of Experience", value: 4 },
  { label: "Projects Completed", value: 70 },
];

const reviews = [
  {
    name: "Pankaj Kumawat",
    text: "Great work, Amresh! The website interface you created is exactly what I had in mind. I really appreciate the new animation you added.",
    rating: 5,
  },
  {
    name: "Shree Krishna",
    text: "Good service. Had a great experience and got my website as expected and on time.",
    rating: 5,
  },
  {
    name: "Shruti Ghelani",
    text: "Professional and reliable developer. Delivered the project within the deadline with great quality.",
    rating: 5,
  },
  {
    name: "NISARG INSTITUTE",
    text: "Excellent website development service! Professional design, fast performance, and great support throughout the project. Highly recommended for quality and timely work.",
    rating: 5,
  },
  {
    name: "Surya Singh",
    text: "Professional in Website, Excellent and fast work in competitive price. Great expertise.",
    rating: 5,
  },
  {
    name: "Prasnna Ghadshi",
    text: "Best freelance Website developer in Virar. I am highly recommend to all small businesses and startup companies, you should get your website built by Amresh Prajapati. His after project services is very excellent.",
    rating: 5,
  },
];

const projects = [
  {
    num: "01",
    img: completeImg,
    cat: "Freelancing Project",
    title: "Complete Energy Group – Solar Solutions Website",
    desc: "A professional and conversion-focused website for solar products, batteries and EV solutions.",
  },
  {
    num: "02",
    img: stellImg,
    cat: "Freelancing Project",
    title: "Stella Designs – Creative Studio Website",
    desc: "A modern website for a 360° design studio focused on wall graphics, signage and placemaking.",
  },
];

/* ─── SEO: JSON-LD structured data for the homepage ──────────
   Built only from content that is actually rendered on this
   page (services, skills, projects, reviews) — nothing invented. */
const homeSchema = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "ProfessionalService",
      "@id": "https://freelanceramreshprajapatimumbai.in/#business",
      name: "Amresh Prajapati - Website Developer",
      url: "https://freelanceramreshprajapatimumbai.in/",
      telephone: "+91-9004068462",
      email: "info@freelanceramreshprajapatimumbai.in",
      image:
        "https://freelanceramreshprajapatimumbai.in/amresh-prajapati-web-developer-mumbai-og.webp",
      description:
        "Amresh Prajapati is a website developer and freelancer in Mumbai offering web design, React, WordPress and SEO services.",
      address: {
        "@type": "PostalAddress",
        streetAddress:
          "FRF4+572, J56 Rustomjee Ave, Evershine Global City, Dongarpada",
        addressLocality: "Virar West, Vasai-Virar",
        addressRegion: "Maharashtra",
        postalCode: "401303",
        addressCountry: "IN",
      },
      areaServed: ["Mumbai", "Virar", "Vasai", "Nallasopara", "Palghar"],
      geo: { "@type": "GeoCoordinates", latitude: 19.3906, longitude: 72.837 },
      openingHours: "Mo-Su 00:00-23:59",
      priceRange: "₹₹",
      sameAs: [
        "https://www.instagram.com/amreshprajapatiwebdevloper/",
        "https://www.linkedin.com/in/amresh-prajapati/",
        "https://www.facebook.com/amreshprajapatiwebdevloper",
      ],
      makesOffer: services.map((s) => ({
        "@type": "Offer",
        itemOffered: { "@type": "Service", name: s.title, description: s.desc },
      })),
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: (
          reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length
        ).toFixed(1),
        reviewCount: reviews.length,
      },
      review: reviews.map((r) => ({
        "@type": "Review",
        author: { "@type": "Person", name: r.name },
        reviewRating: {
          "@type": "Rating",
          ratingValue: r.rating,
          bestRating: 5,
        },
        reviewBody: r.text,
      })),
    },
    {
      "@type": "Person",
      "@id": "https://freelanceramreshprajapatimumbai.in/#person",
      name: "Amresh Prajapati",
      jobTitle: "Website Developer & Freelancer",
      url: "https://freelanceramreshprajapatimumbai.in/",
      image: "https://freelanceramreshprajapatimumbai.in/og-image.jpg",
      email: "info@freelanceramreshprajapatimumbai.in",
      telephone: "+919004068462",
      worksFor: { "@id": "https://freelanceramreshprajapatimumbai.in/#business" },
      knowsAbout: skills.map((s) => s.name),
      sameAs: [
        "https://www.instagram.com/amreshprajapatiwebdevloper/",
        "https://www.linkedin.com/in/amresh-prajapati/",
        "https://www.facebook.com/amreshprajapatiwebdevloper",
      ],
    },
    {
      "@type": "WebSite",
      "@id": "https://freelanceramreshprajapatimumbai.in/#website",
      url: "https://freelanceramreshprajapatimumbai.in/",
      name: "Amresh Prajapati",
      publisher: { "@id": "https://freelanceramreshprajapatimumbai.in/#person" },
      inLanguage: "en-IN",
    },
    {
      "@type": "WebPage",
      "@id": "https://freelanceramreshprajapatimumbai.in/#webpage",
      url: "https://freelanceramreshprajapatimumbai.in/",
      name: "Amresh Prajapati | Best Website Developer Freelancer in Mumbai",
      description:
        "Amresh Prajapati is a website developer and freelancer in Mumbai offering web design, React, WordPress and SEO services.",
      isPartOf: { "@id": "https://freelanceramreshprajapatimumbai.in/#website" },
      about: { "@id": "https://freelanceramreshprajapatimumbai.in/#business" },
      breadcrumb: {
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Home",
            item: "https://freelanceramreshprajapatimumbai.in/",
          },
        ],
      },
    },
  ],
};

export default function Home() {
  const location = useLocation();

  const [scrolled, setScrolled] = useState(false);
  const statsRef = useRef(null);
  const [startCount, setStartCount] = useState(false);
  const [counts, setCounts] = useState(stats.map(() => 0));

  const [reviewIndex, setReviewIndex] = useState(0);
  const [perView, setPerView] = useState(3);
  const reviewsPausedRef = useRef(false);

  const reviewMaxIndex = Math.max(0, reviews.length - perView);
  const activeReviewIndex = Math.min(reviewIndex, reviewMaxIndex);

  useEffect(() => {
    const updatePerView = () => {
      const w = window.innerWidth;
      setPerView(w <= 640 ? 1 : 2);
    };
    updatePerView();
    window.addEventListener("resize", updatePerView);
    return () => window.removeEventListener("resize", updatePerView);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      if (!reviewsPausedRef.current) {
        setReviewIndex((idx) =>
          Math.min(idx, reviewMaxIndex) >= reviewMaxIndex
            ? 0
            : Math.min(idx, reviewMaxIndex) + 1
        );
      }
    }, 2000);
    return () => clearInterval(timer);
  }, [reviewMaxIndex]);

  const handleHomeFormSubmit = (e) => {
    e.preventDefault();

    const form = e.currentTarget;

    const name = form.name.value.trim();
    const email = form.email.value.trim();
    const phone = form.phone.value.trim();
    const service = form.service.value.trim();
    const message = form.message.value.trim();

    if (!name || !email || !phone || !service || !message) {
      alert("Please fill all required fields.");
      return;
    }

    const whatsappText = `Hello Amresh,

New enquiry from website:

Name / Company: ${name}
Email: ${email}
Phone: ${phone}
Service: ${service}
Message: ${message}`;

    alert("Thank you! Your form has been submitted successfully.");

    form.reset();

    window.open(
      `https://wa.me/919004068462?text=${encodeURIComponent(whatsappText)}`,
      "_blank"
    );
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();

    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setStartCount(true);
          observer.disconnect();
        }
      },
      { threshold: 0.4 }
    );

    if (statsRef.current) observer.observe(statsRef.current);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!startCount) return;

    stats.forEach((stat, index) => {
      let start = 0;
      const end = stat.value;
      const duration = 1500;
      const increment = end / (duration / 16);

      const counter = setInterval(() => {
        start += increment;

        if (start >= end) {
          start = end;
          clearInterval(counter);
        }

        setCounts((prev) => {
          const updated = [...prev];
          updated[index] = Math.floor(start);
          return updated;
        });
      }, 16);
    });
  }, [startCount]);

  return (
    <>
      <Helmet>
        <title>Freelance Website Developer in Mumbai | Amresh Prajapati</title>
        <meta
          name="description"
          content="Hire Amresh Prajapati — freelance website developer & designer in Mumbai. 4+ years experience, 70+ projects. Expert in React JS, WordPress, SEO & ecommerce websites. Get a free quote today!"
        />
        <link rel="canonical" href="https://freelanceramreshprajapatimumbai.in/" />

        {/* Extra SEO meta */}
        <meta
          name="keywords"
          content="website developer Mumbai, freelance web developer Mumbai, website designer Mumbai, React developer Mumbai, WordPress developer Mumbai, SEO expert Mumbai, best website developer in Mumbai, freelance website design Mumbai, ecommerce website developer Mumbai, business website design Mumbai, Amresh Prajapati"
        />
        <meta name="robots" content="index, follow" />
        <meta name="googlebot" content="index, follow" />
        <meta name="author" content="Amresh Prajapati" />
        <meta name="language" content="English" />
        <meta name="revisit-after" content="7 days" />

        {/* Geo targeting */}
        <meta name="geo.region" content="IN-MH" />
        <meta name="geo.placename" content="Mumbai" />
        <meta name="geo.position" content="19.076;72.8777" />
        <meta name="ICBM" content="19.076, 72.8777" />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta
          property="og:title"
          content="Freelance Website Developer in Mumbai | Amresh Prajapati"
        />
        <meta
          property="og:description"
          content="Hire Amresh Prajapati — freelance website developer & designer in Mumbai. 4+ years experience, 70+ projects. Expert in React JS, WordPress, SEO & ecommerce websites. Get a free quote today!"
        />
        <meta property="og:url" content="https://freelanceramreshprajapatimumbai.in/" />
        <meta property="og:image" content="https://freelanceramreshprajapatimumbai.in/og-image.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:locale" content="en_IN" />
        <meta property="og:site_name" content="Amresh Prajapati" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Freelance Website Developer in Mumbai | Amresh Prajapati"
        />
        <meta
          name="twitter:description"
          content="Hire Amresh Prajapati — freelance website developer & designer in Mumbai. 4+ years experience, 70+ projects. Expert in React JS, WordPress, SEO & ecommerce websites. Get a free quote today!"
        />
        <meta name="twitter:image" content="https://freelanceramreshprajapatimumbai.in/og-image.jpg" />

        {/* Favicons / theme */}
        <meta name="theme-color" content="#0d0d0f" />

        {/* Structured data */}
        <script type="application/ld+json">{JSON.stringify(homeSchema)}</script>
      </Helmet>

      <div className="portfolio-page">
        <div className="portfolio-shell">
          <aside className="portfolio-sidebar">
            <motion.div
              className="profile-card"
              initial={{ opacity: 0, x: -40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="profile-card__top">
                <div className="profile-card__logo">
                  <img src={logoImg} alt="Amresh Prajapati Logo" />
                </div>
                <span className="profile-card__role">Web Design &amp; Developer</span>
              </div>

              <div className="profile-card__avatar-wrap">
                <div className="profile-card__avatar">
                  <img
                    src={amreshprajapatiImg}
                    alt="Amresh Prajapati - Website Developer"
                  />
                </div>
                <div className="profile-card__ring" />
              </div>

              <div className="profile-card__info">
                <p>
                  <FaEnvelope className="profile-card__icon" />
                  info@freelanceramreshprajapatimumbai.in
                </p>
                <p>
                  <FaMapMarkerAlt className="profile-card__icon" />
                  Mumbai, Maharashtra
                </p>
                <p className="profile-card__copy">@2026 Amresh | All Rights Reserved</p>

                <a href="tel:+919004068462" className="profile-card__phone">
                  <FaPhone /> +91-9004068462
                </a>
              </div>

              <div className="profile-card__socials">
                <a
                  href="https://www.instagram.com/amreshprajapatiwebdevloper/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Instagram"
                >
                  <FaInstagram />
                </a>
                <a
                  href="https://www.linkedin.com/in/amresh-prajapati/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="LinkedIn"
                >
                  <FaLinkedinIn />
                </a>
                <a
                  href="https://www.facebook.com/amreshprajapatiwebdevloper"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Facebook"
                >
                  <FaFacebookF />
                </a>
                <a
                  href="https://api.whatsapp.com/send/?phone=919004068462"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="WhatsApp"
                >
                  <FaWhatsapp />
                </a>
              </div>

              <a href="#contact" className="btn btn--primary">
                <FaEnvelope /> Hire Me
              </a>
            </motion.div>
          </aside>

          <main className="portfolio-main">
            <div className={`home-header ${scrolled ? "scrolled" : ""}`}>
              <nav className="nav-links">
                <Link to="/" className={location.pathname === "/" ? "active" : ""}>
                  Home
                </Link>

                <Link to="/about" className={location.pathname === "/about" ? "active" : ""}>
                  About Us
                </Link>

                <Link
                  to="/services"
                  className={location.pathname === "/services" ? "active" : ""}
                >
                  Services
                </Link>

                <Link
                  to="/projects"
                  className={location.pathname === "/projects" ? "active" : ""}
                >
                  Projects
                </Link>

                <Link
                  to="/contact"
                  className={location.pathname === "/contact" ? "active" : ""}
                >
                  Contact Us
                </Link>

                <Link to="/contact" className="hire-btn">
                  Hire Me
                </Link>
              </nav>
            </div>

            <section className="section section--first" id="home">
              <motion.div
                className="hero__intro"
                initial={{ opacity: 0, x: 40 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.7 }}
              >
                <span className="section__tag section__tag--dark">
                  <FaHome /> INTRODUCE
                </span>

                <h1 className="hero__heading">
                  Hey There,
                  <br />
                  I&apos;m <span className="hero__accent">Amresh Prajapati,</span>
                  <br />A Passionate Website Developer Based In Mumbai, India
                </h1>

                <p className="hero__sub">
                  I design and build modern, responsive and business-focused websites
                  with clean UI, strong performance and premium visual impact.
                </p>

                <div className="hero__stats" ref={statsRef}>
                  {stats.map((s, i) => (
                    <div className="hero__stat stat-card" key={s.label}>
                      <span className="hero__stat-val">{counts[i]}+</span>
                      <span className="hero__stat-label">{s.label}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            </section>

            <section className="section" id="about">
              <motion.div
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <span className="section__tag section__tag--dark">ABOUT</span>
                <h2 className="section__heading">
                  Every Great Design Begins With an Even Better Story
                </h2>

                <p className="section__body">
                  I blend technical expertise with creative design to build visually
                  stunning, high-performing websites that leave a lasting impression. As
                  one of the{" "}
                  <strong>best website developers in Mumbai</strong>, I work seamlessly
                  across front-end, back-end and full-stack development — from crafting
                  pixel-perfect UI to building scalable custom software — to bring digital
                  ideas to life.
                </p>

                <p className="section__body">
                  As a freelance web developer,{" "}
                  <span className="highlight-text">
                    I bring together digital creativity and technical depth
                  </span>
                  , using React JS, Node.js, PHP and MySQL to deliver everything from fast,
                  SEO-friendly business websites to{" "}
                  <span className="highlight-text">
                    custom CRM and ERP solutions
                  </span>{" "}
                  with automated workflows and dashboards. Every project is built with a
                  strong visual identity, smooth user experience and performance that helps
                  businesses grow online.
                </p>

                <p className="section__body">
                  Ready to elevate your online presence? Let&apos;s collaborate and create
                  something extraordinary together.
                </p>
              </motion.div>
            </section>

            <section className="section" id="services">
              <motion.div
                className="section__header section__header--left"
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
              >
                <span className="section__tag section__tag--dark">SERVICES</span>
                <h2 className="section__heading">My Specializations</h2>
              </motion.div>

              <div className="services-grid">
                {services.map((svc, i) => (
                  <motion.div
                    key={svc.title}
                    className="service-card glass-card"
                    variants={fadeUp}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.08 }}
                  >
                    <div className="service-card__icon">{svc.icon}</div>
                    <div className="service-card__body">
                      <h3>{svc.title}</h3>
                      <p>{svc.desc}</p>
                      <span className="service-card__count">{svc.count}</span>
                    </div>
                  </motion.div>
                ))}
              </div>
            </section>

            <section className="section" id="skills">
              <motion.div
                className="section__header section__header--left"
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
              >
                <span className="section__tag section__tag--dark">MY SKILLS</span>
                <h2 className="section__heading">My Advantages</h2>
                <p className="section__sub section__sub--left">
                  More than 8+ technologies powering my website development work.
                </p>
              </motion.div>

              <div className="skills-grid">
                {skills.map((sk, i) => (
                  <motion.div
                    key={sk.name}
                    className="skill-card glass-card"
                    variants={fadeUp}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.05 }}
                  >
                    <div className="skill-card__icon">{sk.icon}</div>
                    <div className="skill-card__pct">{sk.pct}%</div>
                    <div className="skill-card__name">{sk.name}</div>
                    <div className="skill-card__bar">
                      <motion.div
                        className="skill-card__fill"
                        initial={{ width: 0 }}
                        whileInView={{ width: `${sk.pct}%` }}
                        viewport={{ once: true }}
                        transition={{ duration: 1 }}
                      />
                    </div>
                  </motion.div>
                ))}
              </div>
            </section>

            <section className="section" id="projects">
              <motion.div
                className="section__header section__header--left"
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
              >
                <span className="section__tag section__tag--dark">PORTFOLIO</span>
                <h2 className="section__heading">Featured Projects</h2>
              </motion.div>

              <div className="featured-projects">
                {projects.map((p, i) => (
                  <motion.div
                    key={p.num}
                    className="featured-project glass-card"
                    variants={fadeUp}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                  >
                    <div className="featured-project__badge">
                      <FaCheckCircle /> Project No. {p.num}
                    </div>

                    <div className="featured-project__img-wrap">
                      <img src={p.img} alt={p.title} loading="lazy" />
                      <div className="featured-project__overlay" />
                    </div>

                    <span className="featured-project__cat">{p.cat}</span>
                    <h3>{p.title}</h3>
                    <p>{p.desc}</p>
                  </motion.div>
                ))}
              </div>

              <div className="projects-cta">
                <Link to="#" className="btn btn--primary">
                  View More Projects <FaArrowRight />
                </Link>
              </div>
            </section>

            <section className="section" id="testimonials">
              <motion.div
                className="section__header section__header--left"
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
              >
                <span className="section__tag section__tag--dark">GOOGLE REVIEWS</span>
                <h2 className="section__heading">Trusted by Clients</h2>
                <p className="section__sub section__sub--left">
                  Real feedback from clients on my Google Business Profile.
                </p>
              </motion.div>

              <div
                className="reviews-carousel"
                onMouseEnter={() => (reviewsPausedRef.current = true)}
                onMouseLeave={() => (reviewsPausedRef.current = false)}
              >
                <div className="reviews-viewport">
                  <motion.div
                    className="reviews-track"
                    animate={{ x: `-${(activeReviewIndex * 100) / perView}%` }}
                    transition={{ duration: 0.5, ease: "easeInOut" }}
                  >
                    {reviews.map((r, i) => (
                      <div
                        className="review-slide"
                        key={`${r.name}-${i}`}
                        style={{
                          flex: `0 0 ${100 / perView}%`,
                          maxWidth: `${100 / perView}%`,
                        }}
                      >
                        <div className="review-card glass-card">
                          <div className="review-card__stars">
                            {[...Array(r.rating)].map((_, j) => (
                              <FaStar key={j} />
                            ))}
                          </div>
                          <p className="review-card__text">{r.text}</p>
                          <div className="review-card__author">
                            <div className="review-card__avatar">{r.name[0]}</div>
                            <div>
                              <strong>{r.name}</strong>
                              <span>Google Review</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </motion.div>
                </div>

                <button
                  type="button"
                  className="reviews-arrow reviews-arrow--left"
                  aria-label="Previous reviews"
                  onClick={() =>
                    setReviewIndex(
                      activeReviewIndex <= 0 ? reviewMaxIndex : activeReviewIndex - 1
                    )
                  }
                >
                  <FaArrowLeft />
                </button>
                <button
                  type="button"
                  className="reviews-arrow reviews-arrow--right"
                  aria-label="Next reviews"
                  onClick={() =>
                    setReviewIndex(
                      activeReviewIndex >= reviewMaxIndex ? 0 : activeReviewIndex + 1
                    )
                  }
                >
                  <FaArrowRight />
                </button>

                <div className="reviews-dots">
                  {Array.from({ length: reviewMaxIndex + 1 }).map((_, d) => (
                    <button
                      key={d}
                      type="button"
                      className={d === activeReviewIndex ? "active" : ""}
                      onClick={() => setReviewIndex(d)}
                      aria-label={`Go to review group ${d + 1}`}
                    />
                  ))}
                </div>
              </div>

              <div className="google-review-actions">
                <a
                  href="https://share.google/He2FvGed2fRGZFrZv"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn--primary"
                >
                  See All Google Reviews
                </a>
              </div>
            </section>

            <section className="section" id="contact">
              <div className="contact-form-card glass-card glass-card--strong">
                <span className="section__tag section__tag--dark">CONTACT ME</span>
                <h2 className="contact-form__heading">Let&apos;s Work Together!</h2>

                <form className="contact-form-grid" onSubmit={handleHomeFormSubmit}>
                  <div className="contact-field contact-field--full">
                    <label>
                      Full Name / Company Name <span>*</span>
                    </label>
                    <input
                      name="name"
                      type="text"
                      placeholder="Full Name / Company Name"
                      required
                    />
                  </div>

                  <div className="contact-field">
                    <label>
                      Email <span>*</span>
                    </label>
                    <input
                      name="email"
                      type="email"
                      placeholder="Email Address"
                      required
                    />
                  </div>

                  <div className="contact-field">
                    <label>
                      Your number <span>*</span>
                    </label>
                    <input
                      name="phone"
                      type="tel"
                      placeholder="Your number"
                      required
                    />
                  </div>

                  <div className="contact-field contact-field--full">
                    <label>
                      What Are u Looking For <span>*</span>
                    </label>
                    <select name="service" defaultValue="" required>
                      <option value="" disabled>
                        - Select Services -
                      </option>
                      <option>Website Design</option>
                      <option>Website Development</option>
                      <option>React Website</option>
                      <option>WordPress Website</option>
                      <option>SEO / Marketing</option>
                      <option>Packaging Design</option>
                    </select>
                  </div>

                  <div className="contact-field contact-field--full">
                    <label>
                      Message <span>*</span>
                    </label>
                    <textarea
                      name="message"
                      rows="6"
                      placeholder="Message"
                      required
                    ></textarea>
                  </div>

                  <div className="contact-field contact-field--full">
                    <button type="submit" className="contact-submit-btn">
                      Submit
                    </button>
                  </div>
                </form>
              </div>
            </section>
          </main>
        </div>
      </div>
    </>
  );
}
